import { defineStore } from 'pinia'
import { routerKey, useRoute } from 'vue-router';

import { DesignStore } from '@/front/stores/design_store.js';
import { AnnStore } from '@/front/stores/ann_store.js';
import { PlaybackStore } from '@/front/stores/playback_store.js';
import { PlayerStore } from '@/front/stores/player_store.js';
import { addToUrl } from '@/front/composables/StringComposer';

export const VideoStore = defineStore('videos', {
  state: () => {
    return {
      videos: [],
      loading: true,
      video: {},
      saving: false,
      showSavingAnimation: false,
      errors: [],
      uploadProgress: 0,
      isUploading: false,
      uploadFile: null,
      uploadSource: {
        src: null,
        type: null,
        temporary: true,
      },
      uploadSettings: {
        title: '',
        transcode: true,
        transcribe: true
      },
      pagination: {page: 1},
      openaiCalls: [],
      mobilePreview: false
    }
  },

  actions: {
    async index(path) {
      log.info(2,"VideoStore index")
      this.loading = true
      return this.Api.get(path).then(response => {  
        this.pagination = response.data.pagination;
        this.videos = response.data.videos;        
      }).finally(() => {
        this.loading = false
      })
    },

    async deleteVideo() {
      log.info(2,"🌜 VideoStore deleteVideo", this.video.id)
      return this.Api.destroy(`/videos/${this.video.id}`).then(response => {  
        this.video = {};
      })
    },  

    async downloadSource(id) {
      log.info(2,"🌜 VideoStore downloadSource", id)
      return this.Api.get(`/videos/${id}/download`).then(response => {
        return response.data
      })
    },

    async checkPassword(password) {
      log.info(2,"🌜 VideoStore checkPassword", password)
      return this.Api.get(`/videos/${this.video.id}/validate_password?mspw=${password}`).then(response => {  
        return response.data.valid
      })
    },

    async checkCode(email, code) {
      log.info(2,"🌜 VideoStore checkCode", email, code)
      return this.Api.get(`/videos/${this.video.id}/validate_list?email=${email}&code=${code}`).then(response => {  
        log.info(2,"🔊 VideoStore checkCode", response.data)
        return response.data
      })
    },

    async safeShow(id, args={}) {
      const hasVideo = !!this.video
      const matchesVideo = hasVideo && (this.video.token == id || this.video.id == id)
      if (matchesVideo) {
        if (args.manage && !this.video.manage) {
          log.info(2,"🔄 VideoStore safeShow upgrading to manage context")
          return this.show(id, args)
        }
        log.info(2,"✅ VideoStore safeShow video is up to date")
        return
      }
      // log.info(2,"💦 VideoStore safeShow calling show")
      return this.show(id, args)
    },

    async togglePublished(id) {
      log.info(2,"🌜 VideoStore togglePublished", id, "set to", !this.video.published)
      return this.Api.put(`/videos/${this.video.id}/published`, { published: !this.video.published }).then(response => {  
        this.video.published = response.data.published
      }).catch(error => {
        log.info(2,"🔴 VideoStore togglePublished error", error)
        throw error.response.data.error
      })
    },

    async publishVideoById(id) {
      log.info(2,"🌜 VideoStore publishVideoById", id)
      return this.Api.put(`/videos/${id}/published`, { published: true }).then(response => {  
        log.info(2,"🔊 VideoStore publishVideoById done", response.data)
      }).catch(error => {
        log.info(2,"🔴 VideoStore togglePublished error", error)
        throw error.response.data.error
      })
    },

    async updateDuration() {  
      const playerStore = PlayerStore();

      if (!playerStore.videoElement) {
        return
      }
      const duration = Math.floor(playerStore.videoElement.duration)
      log.info(2,"🌜 VideoStore updateDuration", this.video.id, duration)
      return this.Api.put(`/videos/${this.video.id}`, { duration: duration }).then(response => {  
        this.video.duration = duration
        // this.video = response.data.video;        
      }).finally(() => {
        this.saving = false
      })
    },

    async importMagicMenu(token) {
      log.info(2,"🌜 VideoStore importMagicMenu", token)
      return this.Api.post(`/videos/${this.video.id}/import_magic_menu`, { token: token }).then(response => {
        log.info(2,"🔊 VideoStore importMagicMenu done")
      })
    },

    async importEndMenu(token) {  
      log.info(2,"🌜 VideoStore importEndMenu", token)
      return this.Api.post(`/videos/${this.video.id}/import_end_menu`, { token: token }).then(response => {
        log.info(2,"🔊 VideoStore importEndMenu done")
      })
    },

    async importDesign(source, argument) {
      log.info(2,"🌜 VideoStore importDesign", source, argument)
      return this.Api.post(`/videos/${this.video.id}/import_design`, { source: source, argument: argument }).then(response => {
        log.info(2,"🔊 VideoStore importDesign done")
      })
    },

    async show(id, args={}) {
      log.info(2,"🌜 VideoStore SHOW", id, args)
      
      // Check for preloaded data first
      if (!args.manage && window.__PRELOADED_VIDEO_DATA__) {
        log.info(2,"🚀 Using preloaded video data from server");
        return this.handlePreloadedData(window.__PRELOADED_VIDEO_DATA__, args);
        
      }
      const previewToken = this.router.currentRoute.query.preview_token
      const designStore = DesignStore();
      const annStore = AnnStore();
      const playbackStore = PlaybackStore();
      
      // Fallback to API call if no preloaded data
      log.info(2,"📡 Fetching video data via API");
      log.info(2,"🔊 VideoStore SHOW args", args)
      log.info(2,"🔊 VideoStore SHOW playbackStore.variables", playbackStore.variables)
      let item_id = args.item_id || playbackStore.getVariable('item_id')
      // log.info(2,"🔊 VideoStore SHOW item_id", item_id)

      // get the preview_token from the url
      annStore.anns = []
      annStore.active = new Map()
      annStore.resetState()
      this.loading = true
      // this.video = {}
      let url = `/videos/${id}`
      if (!args.manage) {
        url += `/playback`
        if (previewToken) {
          url = addToUrl(url, `preview_token=${previewToken}`)
        }
        if (args.noview) {
          url = addToUrl(url, `noview=1`)
        }
        if (args.plink_id) {
          url = addToUrl(url, `plink_id=${args.plink_id}`)
        } else if (args.password) {
          url = addToUrl(url, `mspw=${args.password}`)
        } else if (item_id) {
          log.info(2,"🔊 VideoStore addToUrl item_id", item_id)
          url = addToUrl(url, `item_id=${item_id}`)
        } 
      }
      log.info(2,"🌜 VideoStore SHOW url", url)
      const playerStore = PlayerStore();
      return this.Api.get(url).then(response => {  
        // log.info(2,"🔊 VideoStore loaded", response.data.video)
        // log.info(2,"🔊 VideoStore blocked", response.data.video.accessible)
        // If branching during real playback and blocked, don't switch
        if (response.data.video.accessible.unpublished && !response.data.video.manage && playerStore.branching) {
          log.info(2,"🔊 VideoStore blocked", response.data.video.accessible.blocked)
          return
        }
        this.video = {}
        this.video = response.data.video;
        playerStore.initializeTextTracks();
        
        if (this.video.design?.color) {
          // log.info(2,"🔊 VideoStore set color", this.video.design.color)
          document.documentElement.style.setProperty('--user-theme-color', this.video.design.color)
        }
        if (this.video.accessible.blocked && !this.video.manage) {
          log.info(2,"🔊 VideoStore blocked", this.video.accessible.blocked)
          return
        }
        if (this.video.manage) {
          designStore.show(this.video.id)
        }
        // log.info(2,"🔊 VideoStore show annStore.index", this.video.token)
        annStore.index(this.video.token)
      }).catch(error => {
        log.info(2,"🔴 VideoStore show error", error)
        if (error?.response?.status === 404) {
          log.info(2,"🔴 VideoStore show not found", id)
          this.video = { blocked: true }
        }
        if (args.manage) {
          this.router.push('/videos')
        }
      // INSERT_YOUR_CODE
        return Promise.reject(error)
      }).finally(() => {
        this.loading = false
        // log.info(2,"🌜 VideoStore SHOW finally done")
      })
    },
    
    // Handle preloaded video data from server
    async handlePreloadedData(data, args) {
      log.info(2,"🎯 Processing preloaded video data");
      const designStore = DesignStore();
      const annStore = AnnStore();
      const playerStore = PlayerStore();
      
      try {
        this.loading = true;
        annStore.anns = [];
        
        // Parse the data if it's a string
        const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
        // Extract video from wrapper if present (matches API response structure)
        const videoData = parsedData.video || parsedData;
        log.info(2,"📦 Preloaded data parsed:", videoData);
        
        // Clear the preloaded data to prevent reuse
        // window.__PRELOADED_VIDEO_DATA__ = null;
        
        // Check for blocking conditions
        if (videoData.accessible?.unpublished && !videoData.manage && playerStore.branching) {
          log.info(2,"🔊 VideoStore blocked (preloaded)", videoData.accessible.blocked);
          return;
        }
        
        // Set the video data
        this.video = {};
        this.video = videoData;
        playerStore.initializeTextTracks();
        // Apply theme color if available
        if (this.video.design?.color) {
          log.info(2,"🎨 Setting theme color from preloaded data");
          document.documentElement.style.setProperty('--user-theme-color', this.video.design.color);
        }


        
        // Check accessibility
        if (this.video.accessible?.blocked && !this.video.manage) {
          log.info(2,"🔊 VideoStore blocked (preloaded)", this.video.accessible.blocked);
          return;
        }


        
        // Initialize related stores
        // designStore.show(this.video.id);
        // Don't pass token if we have preloaded data to use preloaded annotations
        annStore.index();
        
        log.info(2,"✅ Preloaded video data processed successfully");
        // Reset preloaded data
        window.__PRELOADED_VIDEO_DATA__ = null;
      } catch (error) {
        log.info(1,"❌ Error processing preloaded data:", error);
        // Fallback to API call if preloaded data fails
        log.info(2,"🔄 Falling back to API call");
        return this.show(this.router.currentRoute.params.id, args);
      } finally {
        this.loading = false;
      }
    },

    async destroy(id) {      
      return this.Api.destroy(`/videos/${id}`).then(response => {  
        this.errors = [];
      }).catch(error => {
        this.errors = error.response?.data?.errors || [error.message];
        return false;
      }) 
    },

    async update() {
      this.saving = true
      this.showSavingAnimation = true
      this.errors = []
      log.info(2,"Updating video", this.video)
      const videoWithoutTranscript = { ...this.video }
      delete videoWithoutTranscript.transcript
      return this.Api.put(`/videos/${this.video.id}`, videoWithoutTranscript).then(response => {  
        this.video = response.data.video;   
        return true;     
      }).catch(error => {
        const serverErrors = error.response?.data?.errors
        this.errors = serverErrors || [error.message]
        log.info(2,"🔴 VideoStore update error", this.errors)
        return false
      }).finally(() => {
        this.saving = false
        setTimeout(() => {
          this.showSavingAnimation = false
        }, 1500)
      })
    },

    async updateSource() {
      return this.Api.put(`/sources/${this.video.source.id}`, this.video.source).then(response => {  
        log.info(2,"🔊 VideoStore updateSource", response.data)
      })
    },

    async recheckSourceReachability() {
      log.info(2,"🌜 VideoStore recheckSourceReachability", this.video?.source?.id)
      return this.Api.post(`/sources/${this.video.source.id}/recheck_reachability`).then(response => {
        if (this.video?.source) {
          this.video.source.is_unreachable = response.data.source.is_unreachable
        }
        return response.data
      })
    },

    async refreshWistiaAssetsFromPlaybackError(failingUrl) {
      if (!this.video?.source?.id || !this.video?.source?.wistia_id) {
        return { refreshed: false, reason: "not_wistia_source" }
      }
      log.info(2,"🌜 VideoStore refreshWistiaAssetsFromPlaybackError", this.video.source.id, failingUrl)
      return this.Api.post(`/sources/${this.video.source.id}/refresh_wistia_assets`, {
        failing_url: failingUrl,
      }).then(response => {
        return response.data
      })
    },

    async createTranscript() {
      return this.Api.post(`/videos/${this.video.id}/transcribe`).then(response => {  
        log.info(2,"🔊 VideoStore createTranscript", response.data)
      })
    },

    async clearTranscript() {
      return this.Api.post(`/videos/${this.video.id}/clear_transcript`).then(response => {  
        log.info(2,"🔊 VideoStore clearTranscript", response.data)
        this.video.transcript = null
      })
    },

    async updateTranscript() {
      return this.Api.put(`/transcripts/${this.video.transcript.id}`, { chunks: this.video.transcript.chunks }).then(response => {  
        log.info(2,"🔊 VideoStore updateTranscript", response.data)
        this.show(this.video.token, { manage: true })
      })
    },

    async deleteTranscript() {
      return this.Api.destroy(`/transcripts/${this.video.transcript.id}`).then(response => {  
        log.info(2,"🔊 VideoStore deleteTranscript", response.data)
      })
    },

    async duplicate(id, title, group_id, copy_interactions) {
      log.info(2,"🌜 VideoStore duplicate", id, title, group_id, copy_interactions)
      return this.Api.post(`/videos/${id}/duplicate`, { title: title, group_id: group_id, copy_interactions: copy_interactions } ).then(response => {  
        return response.data.token
      })
    },

    async clearActivity() {
      return this.Api.post(`/videos/${this.video.id}/clear`).then(response => {  
        log.info(2,"🔊 VideoStore clearActivity done", response.data)
      })
    },

    async getBranching() {
      return this.Api.get(`/videos/${this.video.id}/branches`).then(response => {  
        log.info(2,"🔊 VideoStore getBranching done", response.data)
        return response.data
      })
    },

    async updateChapter(chapter) {
      const playerStore = PlayerStore();
      return this.Api.put(`/chapters/${chapter.id}`, { chapter: chapter }).then(response => {  
        log.info(2,"🔊 VideoStore updateChapter done", response.data)
        this.video.chapters = this.video.chapters.map(c => c.id == chapter.id ? response.data.chapter : c);
        playerStore.initializeTextTracks();
      })
    },

    async createChapter(chapter) {
      const playerStore = PlayerStore();
      log.info(2,"🌜 VideoStore createChapter", chapter)
      return this.Api.post(`/videos/${this.video.id}/chapters`, { chapter: { start: chapter.start, title: chapter.title } }).then(response => {  
        this.video.chapters.push(response.data.chapter);
        playerStore.initializeTextTracks();

        // Update chapters textrack url
      })
    },

    async deleteChapter(id) {
      const playerStore = PlayerStore();
      return this.Api.destroy(`/chapters/${id}`).then(response => {  
        this.video.chapters = this.video.chapters.filter(chapter => chapter.id != id);
        playerStore.initializeTextTracks();
      })
    },

    async createSubtitle(language, url) {
      return this.Api.post(`/videos/${this.video.id}/subtitles`, { subtitle: { label: language, language: language, url: url, video_id: this.video.id } }).then(response => {  
        this.video.subtitles.push(response.data.subtitle);
      })
    },

    async deleteSubtitle(id) {
      return this.Api.destroy(`/subtitles/${id}`).then(response => {  
        this.video.subtitles = this.video.subtitles.filter(subtitle => subtitle.id != id);
      })
    },

    async getOpenaiCalls() {
      log.info(2,"🌜 VideoStore getOpenaiCalls", this.video.id)
      return this.Api.get(`/videos/${this.video.id}/openai_calls`).then(response => {  
        log.info(2,"🔊 VideoStore getOpenaiCalls", response.data)
        this.openaiCalls = response.data;
      })
    },

    async createFromKey(key) {
      log.info(2,"🌜 VideoStore createFromKey", key, this.uploadFile.name)
      const args = { 
        key: key, 
        name: this.uploadFile.name, 
        size: this.uploadFile.size,
        title: this.uploadSettings.title,
        transcode: this.uploadSettings.transcode,
        transcribe: this.uploadSettings.transcribe,
      }
      log.info(2,"🌜 VideoStore createFromKey args", args)
      return this.Api.post('/videos/create_from_file', args).then(response => {  
        this.video = response.data.video; 
        log.info(2,"UploadFile value", this.uploadFile)       
        this.uploadSource = {
          src: URL.createObjectURL(this.uploadFile),
          type: this.uploadFile.type,
          temporary: true
        }
        log.info(2,"UploadSource", this.uploadSource)
      }).finally(() => {
        this.loading = false
      })
    },

    async getTranscript() {
      return this.Api.get(`/videos/${this.video.id}/transcript`).then(response => {  
        log.info(2,"🔊 VideoStore getTranscript", response.data)
        this.video.transcript = response.data.transcript;
      })
    },

    async getPurchaseLink(id) {
      const playbackStore = PlaybackStore();
      const playerStore = PlayerStore();

      return this.Api.get(`/videos/${id}/purchase?viewer_id=${playbackStore.viewer?.id}&t=${playerStore.fulltime}`).then(response => {  
        log.info(2,"🔊 VideoStore getPurchaseLink", response.data)
      })
    }
  }
})
